import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Lock, User, Loader2, Eye, EyeOff, ShieldCheck, ArrowRight } from 'lucide-react';

// --- PROFESSIONAL LOGO IMPORT ---
import logoImg from '../uploads/logo.png';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  /**
   * AUTO-REDIRECT LOGIC (Hardened)
   * Removed 'super_admin' to match the finalized MongoDB schema
   */
  useEffect(() => {
    if (!authLoading && user) {
      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    
    setLoading(true);
    try {
      const response = await login(username, password);
      
      toast({
        title: 'Authentication Successful',
        description: `Welcome back, ${response.username}!`,
      });

      // Role-based navigation sync
      if (response.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard'); 
      }
      
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.response?.data?.error || 'Invalid username or password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 selection:bg-indigo-100">
      {/* Background Decorative Elements */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-100/50 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md border-none shadow-[0_20px_50px_rgba(8,_112,_184,_0.07)] bg-white/80 backdrop-blur-xl z-10 rounded-3xl overflow-hidden ring-1 ring-white">
        {/* Progress Bar */}
        {loading && <div className="h-1 bg-indigo-600 animate-pulse absolute top-0 left-0 w-full" />}
        
        <CardHeader className="space-y-2 text-center pb-6 pt-10">
          <div className="mx-auto mb-4 group">
            <img 
              src={logoImg} 
              alt="Whatsappbot" 
              className="w-20 h-20 rounded-2xl object-contain shadow-lg transform transition-transform group-hover:scale-105 duration-300"
              onError={(e) => {
                e.target.src = 'https://placehold.co/200x200?text=Logo';
              }}
            />
          </div>
          <CardTitle className="text-3xl font-black tracking-tight text-slate-900">Sign In</CardTitle>
          <CardDescription className="text-slate-500 font-medium">
            Enter your credentials to access your control panel
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            <div className="space-y-1.5">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Username</label>
              <div className="relative group">
                <User className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                <Input 
                  className="pl-12 h-12 border-slate-200 focus:ring-indigo-500 bg-white/50 focus:bg-white transition-all rounded-2xl font-medium"
                  placeholder="admin_yuvraj"
                  type="text" 
                  value={username} 
                  onChange={(e) => setUsername(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <div className="flex justify-between items-center ml-1">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">Password</label>
                <Link to="/forgot-password" size="sm" className="text-xs font-bold text-indigo-600 hover:text-indigo-700">Recovery?</Link>
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                <Input 
                  className="pl-12 pr-12 h-12 border-slate-200 focus:ring-indigo-500 bg-white/50 focus:bg-white transition-all rounded-2xl font-medium"
                  placeholder="••••••••"
                  type={showPassword ? "text" : "password"} 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-3.5 text-slate-400 hover:text-indigo-600 focus:outline-none transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            
            <Button 
              disabled={loading}
              type="submit" 
              className="w-full h-14 bg-indigo-600 hover:bg-indigo-700 text-white transition-all font-black text-lg mt-6 shadow-[0_10px_20px_rgba(79,_70,_229,_0.2)] hover:shadow-indigo-300 rounded-2xl flex items-center justify-center gap-2 group active:scale-[0.98]"
            >
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5" />
                  Authenticate
                  <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </>
              )}
            </Button>
          </form>
        </CardContent>
        
        <CardFooter className="flex flex-col items-center gap-4 border-t border-slate-50 pt-8 pb-10">
          <p className="text-sm font-medium text-slate-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-indigo-600 font-black hover:text-indigo-800 transition-colors">
              Create ID
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}